import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Globe, CheckCircle } from 'lucide-react';
import { Modal } from '~/components/ui/Modal';
import { setIsCommunityApproved } from '~/lib/stores/netlify';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';
import { useTranslation } from '~/lib/hooks/useTranslation';

export interface FormData {
  title: string;
  description: string;
  category: string;
  authorName: string;
  promptUsed: string;
  url: string;
  op: 'append' | 'get' | '';
  isApproved: boolean;
  thumbnailUrl: string;
}

interface CommunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  deployedUrl: string | null;
  screenShotUrl: string | null;
  projectName: string;
  projectDescription: string;
  authorEmail: string;
  prompt: string;
}

const CATEGORIES = ['SaaS', 'Website', 'Internal tools', 'Games', 'Custom'];

export default function CommunityModal({
  isOpen,
  onClose,
  deployedUrl,
  screenShotUrl,
  projectName,
  projectDescription,
  authorEmail,
  prompt,
}: CommunityModalProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    title: 'Untitled',
    description: '',
    category: '',
    authorName: '',
    promptUsed: '',
    url: '',
    op: '',
    isApproved: false,
    thumbnailUrl: '',
  });
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [agreedToPolicies, setAgreedToPolicies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const sanitizedPrompt = prompt
        .replace(/<figma_meta_data>[\s\S]*?<\/figma_meta_data>/g, '')
        .replace(/<supabase_context>[\s\S]*?<\/supabase_context>/g, '')
        .trim();

      setFormData((prev) => ({
        ...prev,
        title: projectName,
        description: projectDescription,
        authorName: authorEmail,
        promptUsed: sanitizedPrompt,
      }));
    }
  }, [isOpen, projectName, projectDescription, authorEmail, prompt]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  if (!isOpen) {
    return null;
  }

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCategorySelect = (category: string) => {
    setFormData((prev) => ({ ...prev, category }));

    if (category === 'Custom') {
      setShowCustomCategoryInput(true);
    } else {
      setShowCustomCategoryInput(false);
      setCustomCategory('');
    }

    setIsCategoryDropdownOpen(false);
  };

  const handleSubmit = async () => {
    const category = showCustomCategoryInput ? customCategory : formData.category;

    const body = {
      ...formData,
      category,
      url: deployedUrl,
      op: 'append',
      thumbnailUrl: screenShotUrl || 'https://ibb.co/67X3xfSV',
    };

    if (!agreedToPolicies || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      const res = await fetch('/api/dualiteCommunity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      console.log('[communityAPI]: ', res);

      const { data } = (await res.json()) as { data: string };

      if (res.ok) {
        setIsCommunityApproved(true);
        setCurrentStep(3);
      } else {
        toast.error(data);
      }
    } catch (err) {
      toast.error('Failed to submit to community');
      Sentry.captureException(err, {
        extra: {
          message: 'Failed to submit to community',
          body,
        },
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      title: 'Untitled',
      description: '',
      category: '',
      authorName: '',
      promptUsed: '',
      url: '',
      op: '',
      isApproved: false,
      thumbnailUrl: '',
    });
    setAgreedToPolicies(false);
    onClose();
  };

  const canProceedStep1 =
    formData.title.trim() &&
    formData.description.trim() &&
    (formData.category === 'Custom' ? customCategory.trim() !== '' : formData.category);
  const canSubmit = formData.authorName.trim() && formData.promptUsed.trim() && agreedToPolicies;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-[880px] px-2">
        <div className="bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full">
          <div className="py-6">
            {currentStep === 3 ? (
              <div className="relative flex items-center justify-center h-[342px]">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-1.5 text-white hover:bg-white/10 transition-colors rounded-md"
                >
                  <X size={16} />
                </button>

                <div className="text-center">
                  <div className="mb-6">
                    <CheckCircle size={64} className="text-white mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {t('modals.community.thankYou', 'Thank you for your submission!')}
                    </h3>
                    <p className="text-white/70 text-sm max-w-md">
                      {t(
                        'modals.community.submissionReceived',
                        "We've received your submission to the Dualite Community. Our team will review it for quality and safety, and we'll reach out via email to confirm the status.",
                      )}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 max-w-md border border-white/20">
                    <h4 className="text-white font-medium text-sm mb-2">
                      {t('modals.community.whatHappensNext', 'What happens next?')}
                    </h4>
                    <ul className="text-white/70 text-xs space-y-1 text-left">
                      <li>{t('modals.community.reviewTime', '• Review process typically takes 1-3 business days')}</li>
                      <li>{t('modals.community.emailConfirm', "• You'll receive email confirmation once approved")}</li>
                      <li>{t('modals.community.goLive', '• Your resource will be live in the community')}</li>
                    </ul>
                  </div>
                  <button
                    onClick={handleClose}
                    className="mt-6 px-6 py-2 bg-white text-black text-sm font-medium rounded-full hover:bg-white/90 transition-colors"
                  >
                    {t('modals.community.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative flex overflow-hidden">
                {/* Sidebar */}
                <div className="w-80 flex flex-col border-r border-white/20">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-white/20">
                    <h2 className="text-base font-medium text-white">
                      {t('modals.community.title', 'Publish to Dualite Community')}
                    </h2>
                    <button
                      onClick={handleClose}
                      className="p-1.5 text-white hover:bg-white/10 transition-colors rounded-md"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {/* Steps */}
                  <div className="p-4 space-y-3">
                    <div
                      className={`flex items-center space-x-3 p-3 rounded-lg text-sm transition-colors ${currentStep === 1 ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border ${
                          currentStep === 1 ? 'bg-white text-black border-white' : 'border-white/30 text-white'
                        }`}
                      >
                        1
                      </div>
                      <span>{t('modals.community.step1', 'Describe your resource')}</span>
                    </div>

                    <div
                      className={`flex items-center space-x-3 p-3 rounded-lg text-sm transition-colors ${currentStep === 2 ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border ${
                          currentStep === 2 ? 'bg-white text-black border-white' : 'border-white/30 text-white'
                        }`}
                      >
                        2
                      </div>
                      <span>{t('modals.community.step2', 'Add the final details')}</span>
                    </div>
                  </div>

                  {/* Resource Preview */}
                  <div className="p-4 mt-auto">
                    <h3 className="text-xs text-white/60 mb-3 font-medium uppercase tracking-wide">
                      {t('modals.community.resourcePreview', 'Resource preview')}
                    </h3>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                      <div className="bg-white/5 rounded-lg h-28 flex items-center justify-center mb-3 border border-white/10">
                        {screenShotUrl ? (
                          <img
                            src={screenShotUrl}
                            alt="Resource preview"
                            className="w-full h-full rounded-lg object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <Globe size={18} className="text-white/40" />
                        )}
                      </div>
                      <h4 className="text-white font-medium text-sm mb-1 truncate">{formData.title}</h4>
                      <p className="text-white/60 text-xs mb-2">By {formData.authorName || 'You'}</p>
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-white/40 text-xs mb-1">
                          {t('modals.community.deployedUrl', 'Deployed URL:')}
                        </p>
                        <p className="text-white/60 text-xs font-mono break-all">{deployedUrl || 'https://'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 overflow-y-auto scrollbar-hide">
                    <div className="p-6">
                      {currentStep === 1 && (
                        <div className="space-y-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-white">
                                {t('modals.community.projectName', 'Project Name')}{' '}
                                <span className="text-red-400">*</span>
                              </label>
                              <span className="text-xs text-white/60">{formData.title.length}/30</span>
                            </div>
                            <input
                              type="text"
                              value={formData.title}
                              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                              className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-colors"
                              maxLength={30}
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-white">
                                {t('modals.community.description', 'Description')}{' '}
                                <span className="text-red-400">*</span>
                              </label>
                              <span className="text-xs text-white/60">{formData.description.length}/100</span>
                            </div>
                            <textarea
                              value={formData.description}
                              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                              placeholder={t(
                                'modals.community.descriptionPlaceholder',
                                'What can people find or learn from this file? Get specific, so the community knows what to expect.',
                              )}
                              className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 h-20 resize-none transition-colors"
                              maxLength={100}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-white mb-2 -mt-4">
                              {t('modals.community.category', 'Category')} <span className="text-red-400">*</span>
                            </label>
                            <div className="relative" ref={dropdownRef}>
                              <button
                                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                                className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-left text-white focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 flex items-center justify-between transition-colors"
                              >
                                <span className={formData.category ? 'text-white' : 'text-white/40'}>
                                  {formData.category || t('modals.community.selectCategory', 'Select a category')}
                                </span>
                                <ChevronDown
                                  size={14}
                                  className={`text-white transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                                />
                              </button>

                              {isCategoryDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#1a1a1a] border border-white/20 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                                  {CATEGORIES.map((category) => (
                                    <button
                                      key={category}
                                      onClick={() => handleCategorySelect(category)}
                                      className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-white/10 transition-colors first:rounded-t-lg last:rounded-b-lg"
                                    >
                                      {category}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {showCustomCategoryInput && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-white mb-2">
                                  {t('modals.community.customCategory', 'Custom Category')}{' '}
                                  <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={customCategory}
                                  onChange={(e) => {
                                    const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                                    setCustomCategory(sanitizedValue);
                                  }}
                                  className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-colors"
                                  maxLength={50}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {currentStep === 2 && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              {t('modals.community.authorName', 'Author Name')} <span className="text-red-400">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.authorName}
                              onChange={(e) => setFormData((prev) => ({ ...prev, authorName: e.target.value }))}
                              placeholder={t('modals.community.authorNamePlaceholder', 'Your name or username')}
                              className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-colors"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              {t('modals.community.promptUsed', 'Prompt Used')} <span className="text-red-400">*</span>
                            </label>
                            <textarea
                              value={formData.promptUsed}
                              onChange={(e) => setFormData((prev) => ({ ...prev, promptUsed: e.target.value }))}
                              placeholder={t(
                                'modals.community.promptPlaceholder',
                                'Describe the prompt or instructions you used to create this resource...',
                              )}
                              className="w-full bg-white/5 hover:bg-white/10  border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 h-32 resize-none transition-colors"
                            />
                          </div>

                          <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                            <label className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={agreedToPolicies}
                                onChange={(e) => setAgreedToPolicies(e.target.checked)}
                                className="w-4 h-4 text-black bg-white border-white rounded focus:ring-white focus:ring-2 mt-0.5"
                              />
                              <span className="text-white/80 text-xs leading-relaxed">
                                {t(
                                  'modals.community.agreeToPolicies',
                                  "I agree to Dualite's community policies. My submission will be reviewed by the Dualite team for any security risks or harmful content before being published.",
                                )}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between p-4 border-t border-white/20">
                    <button
                      onClick={currentStep === 1 ? handleClose : handleBack}
                      className="px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors border border-white/30 rounded-full"
                    >
                      {currentStep === 1 ? t('modals.community.cancel', 'Cancel') : t('modals.community.back', 'Back')}
                    </button>

                    {currentStep === 1 ? (
                      <button
                        onClick={handleNext}
                        disabled={!canProceedStep1}
                        className="px-6 py-2 bg-white text-black text-sm font-medium rounded-full hover:bg-white/90 disabled:bg-white/30 disabled:text-black/40 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('modals.community.next', 'Next')}
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={!canSubmit || isSubmitting}
                        className="px-6 py-2 bg-white text-black text-sm font-medium rounded-full hover:bg-white/90 disabled:bg-white/30 disabled:text-black/40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="i-ph:circle-notch w-4 h-4 animate-spin" />
                            <span>{t('modals.community.submitting', 'Submitting')}</span>
                          </>
                        ) : (
                          t('modals.community.submitToCommunity', 'Submit to Community')
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
